/*
 * The little this site keeps across its own redirects, in the tab's session storage (which
 * /signed-out clears): why the session ended, for the signed-out page, and the typed DELETE
 * confirmation while a fresh sign-in for account deletion is under way. Nothing personal.
 */

const SIGNED_OUT_REASON_KEY = 'dw.signedOutReason';
const DELETE_CONFIRMATION_KEY = 'dw.deleteConfirmation';
const DELETE_CONFIRMATION = 'DELETE';

export type SignedOutReason = 'signed-out' | 'everywhere' | 'deleted' | 'deletion-pending';

const REASONS: readonly SignedOutReason[] = [
  'signed-out',
  'everywhere',
  'deleted',
  'deletion-pending',
];

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode): the pages fall back to their defaults.
  }
}

function remove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing was stored.
  }
}

export function rememberSignedOutReason(reason: SignedOutReason): void {
  write(SIGNED_OUT_REASON_KEY, reason);
}

/** Why the session ended (read, not removed: /signed-out clears session storage itself). */
export function signedOutReason(): SignedOutReason {
  const value = read(SIGNED_OUT_REASON_KEY);
  return REASONS.find((reason) => reason === value) ?? 'signed-out';
}

/** Kept while the fresh sign-in that deletion needs is under way. */
export function rememberDeleteConfirmation(): void {
  write(DELETE_CONFIRMATION_KEY, DELETE_CONFIRMATION);
}

export function forgetDeleteConfirmation(): void {
  remove(DELETE_CONFIRMATION_KEY);
}

/** Whether DELETE was confirmed before a fresh sign-in; consumes it, so a deletion is retried once. */
export function takeDeleteConfirmation(): boolean {
  const confirmed = read(DELETE_CONFIRMATION_KEY) === DELETE_CONFIRMATION;
  remove(DELETE_CONFIRMATION_KEY);
  return confirmed;
}
